/**
 * `canAuthor` — the recursive authorship predicate, frontend mirror of the
 * backend `taskmap.ownership.can_author` (issues #756/#757/#758).
 *
 * Issue #761 (Phase 2). This is the SINGLE PLACE the member-view UI decides
 * "may this person act on this node?" — exactly so we never paint a button
 * that the API will answer with a 403. The backend remains the enforcer
 * (`require_author` → 403); this just reflects the same rule in the surface.
 *
 * The recursive ownership model (§A5 — "sub-leads emerge from the tree"). A
 * membership may author (set state, add sub-tasks, assign within) a node if
 * ANY of the following hold — mirroring `ownership.py` condition-for-condition:
 *
 *   (1) the actor is the team's `lead` or `co_lead`            → full access
 *   (2) the actor is assigned to the target node               → owns the node
 *   (3) the actor is assigned to ANY ancestor of the node      → owns the subtree
 *
 * Pure + dependency-free (operates over the already-built `TreeNode` forest,
 * not the DB): the frontend has the whole tree client-side (§A8 — the whole
 * map is visible to everyone), so the ancestor walk is a local parent-chain
 * lookup rather than a query. No scheduler coupling.
 */

import type { TreeNode } from "./buildTree";

/** The acting member's identity on this team, as the UI knows it. */
export interface Authorship {
  /** The actor's membership id on this team, or null if not resolved yet. */
  membershipId: string | null;
  /** True when the actor is the team `lead` / `co_lead` (condition 1). */
  isLead: boolean;
}

/**
 * Decide whether `who` may author `node`, given the whole `forest` (needed to
 * walk ancestors for condition 3). Mirrors the backend predicate exactly.
 *
 * @param node    the node being acted on.
 * @param who     the acting member's authorship identity.
 * @param forest  the full root forest (the one `buildTree` result).
 */
export function canAuthor(node: TreeNode, who: Authorship, forest: readonly TreeNode[]): boolean {
  return canAuthorReason(node, who, forest) !== "none";
}

/**
 * Why (if at all) `who` may author `node` — the same §A5 predicate as
 * {@link canAuthor}, but returning WHICH condition granted it. This is purely
 * to EXPLAIN the existing model to a person ("you can edit this — you're
 * assigned"); it changes NO permission decision (the boolean `canAuthor` is
 * defined in terms of it). The enforcer is still the backend `require_author`.
 *
 *   - `"lead"`             — the actor is the team lead / co_lead (condition 1)
 *   - `"direct-assignee"`  — assigned to this node (condition 2)
 *   - `"ancestor-assignee"`— assigned to a task above this one (condition 3)
 *   - `"none"`             — none hold; the actor is read-only on this node
 */
export type AuthorReason = "lead" | "direct-assignee" | "ancestor-assignee" | "none";

export function canAuthorReason(
  node: TreeNode,
  who: Authorship,
  forest: readonly TreeNode[],
): AuthorReason {
  // (1) leads have full access — regardless of assignment.
  if (who.isLead) return "lead";

  // A member with no resolved membership owns nothing (read-only).
  if (!who.membershipId) return "none";

  // (2) directly assigned to this node.
  if (node.assigneeIds.includes(who.membershipId)) return "direct-assignee";

  // (3) assigned to any ancestor of this node — owns the subtree beneath it.
  const byId = indexById(forest);
  let current = node.parentId ? byId.get(node.parentId) : undefined;
  const seen = new Set<string>([node.id]); // cycle guard (the tree should never cycle)
  while (current && !seen.has(current.id)) {
    if (current.assigneeIds.includes(who.membershipId)) return "ancestor-assignee";
    seen.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }

  return "none";
}

/** Build an O(1) id → node index over the whole forest (memoise at the call site). */
export function indexById(forest: readonly TreeNode[]): Map<string, TreeNode> {
  const map = new Map<string, TreeNode>();
  const walk = (node: TreeNode) => {
    map.set(node.id, node);
    for (const child of node.children) walk(child);
  };
  for (const root of forest) walk(root);
  return map;
}
